import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";

import { api, type Device } from "@/api/client";
import { DetailPanel, KeyValue, PanelSection } from "@/components/DetailPanel";
import { PageHeader } from "@/components/PageHeader";

const TIER: Record<Device["tier"], { color: string; label: string }> = {
  robust: { color: "text-ok-400", label: "robust" },
  fragile: { color: "text-warn-400", label: "fragile" },
};

const KIND_GLYPH: Record<string, string> = {
  laptop_macos: "▣",
  laptop_windows: "▣",
  smart_tv: "▭",
  voice_assistant: "◉",
  router: "⌬",
  printer: "▤",
  phone: "▯",
};

const KIND_LABEL: Record<string, string> = {
  laptop_macos: "macOS laptop",
  laptop_windows: "Windows laptop",
  smart_tv: "Smart TV",
  voice_assistant: "Voice assistant",
  router: "Router",
  printer: "Printer",
  phone: "Phone",
};

function timeAgo(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  if (ms < 60_000) return `${Math.max(0, Math.floor(ms / 1000))}s ago`;
  if (ms < 3_600_000) return `${Math.floor(ms / 60_000)}m ago`;
  if (ms < 86_400_000) return `${Math.floor(ms / 3_600_000)}h ago`;
  return `${Math.floor(ms / 86_400_000)}d ago`;
}

function Topology({
  devices,
  onSelect,
}: {
  devices: Device[];
  onSelect: (id: string) => void;
}): JSX.Element {
  const router = devices.find((d) => d.kind === "router");
  const others = devices.filter((d) => d.kind !== "router").slice(0, 6);
  const cx = 50;
  const cy = 50;
  const radius = 38;
  return (
    <svg viewBox="0 0 100 100" className="h-56 w-full">
      {others.map((_d, i) => {
        const angle = (i / others.length) * Math.PI * 2 - Math.PI / 2;
        const x = cx + radius * Math.cos(angle);
        const y = cy + radius * Math.sin(angle);
        return (
          <line
            key={i}
            x1={cx}
            y1={cy}
            x2={x}
            y2={y}
            stroke="#1F2940"
            strokeWidth="0.4"
          />
        );
      })}
      <circle cx={cx} cy={cy} r="6" fill="#0F1424" stroke="#22D3EE" strokeWidth="0.8" />
      <circle cx={cx} cy={cy} r="3" fill="#22D3EE">
        <animate
          attributeName="opacity"
          values="0.6;1;0.6"
          dur="1.6s"
          repeatCount="indefinite"
        />
      </circle>
      <text
        x={cx}
        y={cy + 12}
        textAnchor="middle"
        className="fill-chrome-300"
        style={{ fontSize: "3.2px" }}
      >
        {router?.display_name ?? "router"}
      </text>
      {others.map((d, i) => {
        const angle = (i / others.length) * Math.PI * 2 - Math.PI / 2;
        const x = cx + radius * Math.cos(angle);
        const y = cy + radius * Math.sin(angle);
        const fill = d.tier === "robust" ? "#84CC16" : "#F59E0B";
        return (
          <g
            key={d.id}
            onClick={() => onSelect(d.id)}
            style={{ cursor: "pointer" }}
          >
            <circle cx={x} cy={y} r="4" fill="#0F1424" stroke={fill} strokeWidth="0.6" />
            <circle cx={x} cy={y} r="1.5" fill={fill} />
            <text
              x={x}
              y={y + 7.5}
              textAnchor="middle"
              className="fill-chrome-300"
              style={{ fontSize: "2.8px" }}
            >
              {d.display_name.split(" ").slice(0, 2).join(" ")}
            </text>
          </g>
        );
      })}
    </svg>
  );
}

export function DevicesPage(): JSX.Element {
  const { data, isLoading, error } = useQuery({
    queryKey: ["devices"],
    queryFn: api.devices,
  });
  const [activeId, setActiveId] = useState<string | null>(null);
  const active = useMemo(
    () => data?.devices.find((d) => d.id === activeId) ?? null,
    [data, activeId],
  );

  if (isLoading) return <main className="text-chrome-300">Loading…</main>;
  if (error || !data) return <main className="text-crit">Failed to load.</main>;

  if (data.devices.length === 0) {
    return (
      <main>
        <PageHeader
          eyebrow="lan inventory"
          title="Devices"
          subtitle="No devices discovered yet. The shim populates this list from the kernel ARP cache + reverse DNS + OUI vendor lookup."
        />
        <div className="panel p-10 text-center text-sm text-chrome-400">
          Make sure the shim is running and reachable to{" "}
          <code className="code text-cyber-400">orchestrator.homert-staging.criticalassets.com</code>.
          Devices appear within a minute of shim startup if your machine has
          ARP entries for the LAN (which it usually does — generated by normal
          traffic).
        </div>
      </main>
    );
  }

  const robust = data.devices.filter((d) => d.tier === "robust").length;
  const fragile = data.devices.filter((d) => d.tier === "fragile").length;

  return (
    <main>
      <PageHeader
        eyebrow="lan inventory"
        title="Devices"
        subtitle={`${data.devices.length} devices on the household segment. Click any device for full detail.`}
        right={
          <>
            <div className="flex items-center gap-2 rounded border border-ok/30 bg-ok/10 px-3 py-1.5">
              <span className="pulse-dot text-ok" />
              <span className="text-[10px] font-medium uppercase tracking-widest text-ok-400">
                {robust} robust
              </span>
            </div>
            <div className="flex items-center gap-2 rounded border border-warn/30 bg-warn/10 px-3 py-1.5">
              <span className="text-[10px] font-medium uppercase tracking-widest text-warn-400">
                {fragile} fragile
              </span>
            </div>
          </>
        }
      />

      <section className="panel mb-6 p-6">
        <div className="label-overline mb-2">topology · scan source = shim host</div>
        <Topology devices={data.devices} onSelect={setActiveId} />
      </section>

      <section className="grid grid-cols-1 gap-3 lg:grid-cols-2">
        {data.devices.map((d) => {
          const tier = TIER[d.tier];
          return (
            <button
              key={d.id}
              type="button"
              onClick={() => setActiveId(d.id)}
              className="panel flex items-center gap-4 p-4 text-left transition hover:border-cyber-400/40"
            >
              <div className="grid size-12 shrink-0 place-items-center rounded border border-ink-400 bg-ink-600 text-xl text-chrome-300">
                {KIND_GLYPH[d.kind] ?? "▢"}
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span className="truncate font-medium text-chrome-50">
                    {d.display_name}
                  </span>
                  {d.is_shim_host && (
                    <span className="rounded border border-cyber-400/30 bg-cyber-400/10 px-1.5 py-0.5 text-[9px] font-medium uppercase tracking-widest text-cyber-400">
                      shim
                    </span>
                  )}
                </div>
                <div className="mt-1 grid grid-cols-2 gap-x-4 gap-y-0.5 text-[11px] text-chrome-400">
                  <span className="code">{d.ip}</span>
                  <span className="code">{d.mac}</span>
                  <span className="text-chrome-500">{KIND_LABEL[d.kind] ?? d.kind}</span>
                  <span>seen {timeAgo(d.last_seen)}</span>
                </div>
              </div>
              <div className="flex flex-col items-end gap-1">
                <span className="pulse-dot text-ok" />
                <span
                  className={[
                    "text-[10px] font-medium uppercase tracking-widest",
                    tier.color,
                  ].join(" ")}
                >
                  {tier.label}
                </span>
              </div>
            </button>
          );
        })}
      </section>

      <DetailPanel
        open={!!active}
        onClose={() => setActiveId(null)}
        eyebrow="device · detail"
        title={active?.display_name ?? ""}
        accent={active?.tier === "fragile" ? "warn" : "ok"}
      >
        {active && (
          <>
            <PanelSection title="addressing">
              <KeyValue label="ip" mono>
                {active.ip}
              </KeyValue>
              <KeyValue label="mac" mono>
                {active.mac}
              </KeyValue>
              <KeyValue label="device id" mono>
                {active.id}
              </KeyValue>
            </PanelSection>
            <PanelSection title="classification">
              <KeyValue label="kind">
                {KIND_LABEL[active.kind] ?? active.kind}
              </KeyValue>
              <KeyValue label="safety tier">
                <span className={TIER[active.tier].color}>{active.tier}</span>
              </KeyValue>
              <KeyValue label="shim host">
                {active.is_shim_host ? "yes" : "no"}
              </KeyValue>
            </PanelSection>
            <PanelSection title="liveness">
              <KeyValue label="last seen">{timeAgo(active.last_seen)}</KeyValue>
              <KeyValue label="last seen at" mono>
                {new Date(active.last_seen).toLocaleString([], { hour12: false })}
              </KeyValue>
            </PanelSection>
          </>
        )}
      </DetailPanel>
    </main>
  );
}
